import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Data } from '@angular/router';
import { getFullPath } from '../lib/get-full-path';
import { customRenderText } from '../storyblok/custom-render-text';
import { getFormatsFromFormatParam } from '../tools/convert/get-formats-from-format-param';
import { ConversionFormat } from '../tools/convert/templates/conversion-template/conversion-template.component';
import { StoryblokReplaceService } from './storyblok-replace.service';

@Injectable({
  providedIn: 'root',
})
export class ConvertReplaceService {
  private storyblokReplaceService = inject(StoryblokReplaceService);

  private allKeys = [
    { key: 'slug', type: 'slug' },
    { key: 'extension', type: 'extension' },
    { key: 'name', type: 'string' },
    { key: 'category', type: 'string' },
    { key: 'subCategory', type: 'string' },
    { key: 'longDescription', type: 'richText' },
    { key: 'shortDescription', type: 'string' },
    { key: 'convertIcon', type: 'image' },
    { key: 'uploadIcon', type: 'image' },
    { key: 'downloadIcon', type: 'image' },
    { key: 'mainVisual', type: 'image' },
    { key: 'convertIcon__ALT', type: 'imageAlt' },
    { key: 'uploadIcon__ALT', type: 'imageAlt' },
    { key: 'downloadIcon__ALT', type: 'imageAlt' },
    { key: 'mainVisual__ALT', type: 'imageAlt' },
  ] as const;

  constructor() {
    this.storyblokReplaceService.addReplacerFactory(this.replaceFactory.bind(this));
    this.storyblokReplaceService.addFromKeyFactory(this.getFromKeyFactory.bind(this));
  }

  private getFormats(route: ActivatedRouteSnapshot, routeData: Data) {
    const path = getFullPath(route);

    const isConvertTemplatePage = path[0] === 'convert' && path.length === 2;
    if (!isConvertTemplatePage) return;

    const { from: fromFormatSlug, to: toFormatSlug } = getFormatsFromFormatParam(path[1]);
    const formats = routeData.formats as ConversionFormat[];

    if (!formats) return;

    return {
      fromFormat: fromFormatSlug && formats.find(f => f.slug === fromFormatSlug),
      toFormat: toFormatSlug && formats.find(f => f.slug === toFormatSlug),
    };
  }

  private replace(text: string, fromFormat: ConversionFormat, toFormat?: ConversionFormat) {
    if (!text) return text;

    if (!fromFormat) return text;

    if (!toFormat) {
      this.allKeys.forEach(item => {
        const value = this.getReplaceValue(item, fromFormat);
        text = text.replaceAll(`{{format.${item.key}}}`, value);
      });
    } else {
      this.allKeys.forEach(item => {
        const fromValue = this.getReplaceValue(item, fromFormat);
        const toValue = this.getReplaceValue(item, toFormat);
        text = text.replaceAll(`{{fromFormat.${item.key}}}`, fromValue);
        text = text.replaceAll(`{{toFormat.${item.key}}}`, toValue);
      });
    }
    return text;
  }

  private getReplaceValue(item: (typeof this.allKeys)[number], format: ConversionFormat) {
    if (item.type === 'slug') return format.slug;
    if (item.type === 'extension') return format.slug.toUpperCase();
    if (item.type === 'string') return format.content[item.key];
    if (item.type === 'richText') return format.content[item.key] ? customRenderText(format.content[item.key]) : '';
    if (item.type === 'image') return format.content[item.key] ? format.content[item.key].filename : '';
    if (item.type === 'imageAlt') {
      const key = item.key.split('__')[0];
      return format.content[key] ? format.content[key].alt : '';
    }
  }

  public replaceFactory(route: ActivatedRouteSnapshot, routeData: Data) {
    const formats = this.getFormats(route, routeData);
    if (!formats) return;

    const { fromFormat, toFormat } = formats;

    return (value: string) => {
      return this.replace(value, fromFormat, toFormat);
    };
  }

  private getFromKeyFactory(route: ActivatedRouteSnapshot, routeData: Data) {
    const formats = this.getFormats(route, routeData);
    if (!formats) return;

    const { fromFormat, toFormat } = formats;

    return (key: string) => {
      if (key === 'format') return fromFormat;
      if (key === 'fromFormat') return fromFormat;
      if (key === 'toFormat') return toFormat;
    };
  }
}
